import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { api } from '../../api/client';
import './Account.css';

// month names will be loaded from translations via LanguageContext
let MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const TIER_ICONS = { Wanderer:'�', Explorer:'🧭', 'Forest Keeper':'🌲', 'Mountain Guardian':'⛰️', 'Legend of the Peaks':'👑' };
const TIER_COLORS = { Wanderer:'#7a9478', Explorer:'#0C6B37', 'Forest Keeper':'#094A25', 'Mountain Guardian':'#c9853a', 'Legend of the Peaks':'#F8B324' };

function fmtDate(d) { const x = new Date(d); return MONTHS[x.getMonth()] + ' ' + x.getDate() + ', ' + x.getFullYear(); }
function nights(a, b) { return Math.round((new Date(b) - new Date(a)) / 864e5); }
function bkStatus(b) {
  const t = new Date(); t.setHours(0,0,0,0);
  if (b.status === 'cancelled') return 'cancelled';
  if (t < new Date(b.check_in)) return 'upcoming';
  if (t < new Date(b.check_out)) return 'active';
  return 'completed';
}

export default function AccountPage() {
  const { user, signOut, updateProfile } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const locale = language === 'ro' ? 'ro-RO' : 'en-US';

  // load translated months if available
  try { MONTHS = t('account.months'); } catch (e) { /* fallback above */ }
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => { if (user) { setFullName(user.full_name || ''); setPhone(user.phone || ''); } }, [user]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const [br, sr] = await Promise.all([api.getMyBookings(), api.getMyStats()]); setBookings(br.bookings || []); setStats(sr); } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setProfileMsg({ type: '', text: '' });
    const { error } = await updateProfile({ full_name: fullName, phone });
  if (error) setProfileMsg({ type: 'error', text: error.message });
  else { setProfileMsg({ type: 'success', text: t('account.profileSuccess') }); setTimeout(() => setProfileMsg({ type: '', text: '' }), 3000); }
    setSaving(false);
  };

  const handleCancel = async (id) => {
  if (!window.confirm(t('account.bookingCancelPrompt'))) return;
    setCancellingId(id);
    try { await api.cancelBooking(id); fetchData(); } catch (e) { alert(e.message); }
    setCancellingId(null);
  };

  const doSignOut = async () => { await signOut(); navigate('/login'); };

  const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

  const filtered = bookings.filter(b => {
    const s = bkStatus(b);
    if (tab === 'upcoming') return s === 'upcoming' || s === 'active';
    if (tab === 'past') return s === 'completed';
    return s === 'cancelled';
  });

  const loyalty = stats?.loyalty;

  return (
    <div className="acc page-enter">
      {/* ── Hero Banner — Book-style ── */}
      <section className="acc__hero">
        <div className="acc__hero-bg" aria-hidden="true">
          <div className="acc__hero-glow acc__hero-glow--1" />
          <div className="acc__hero-glow acc__hero-glow--2" />
          <div className="acc__hero-glow acc__hero-glow--3" />
          <div className="acc__hero-grid" />
          <div className="acc__hero-veil" />
        </div>

        {/* Floating decorative orbs */}
        <div className="acc__hero-orbs" aria-hidden="true">
          <div className="acc__hero-orb acc__hero-orb--1" />
          <div className="acc__hero-orb acc__hero-orb--2" />
          <div className="acc__hero-orb acc__hero-orb--3" />
        </div>

        {/* Animated corner frames */}
        <div className="acc__hero-frame acc__hero-frame--tl" aria-hidden="true" />
        <div className="acc__hero-frame acc__hero-frame--tr" aria-hidden="true" />
        <div className="acc__hero-frame acc__hero-frame--bl" aria-hidden="true" />
        <div className="acc__hero-frame acc__hero-frame--br" aria-hidden="true" />

        {/* Floating particles */}
        <div className="acc__hero-particles" aria-hidden="true">
          {[...Array(8)].map((_, i) => (
            <span key={i} className={`acc__hero-particle acc__hero-particle--${(i % 3) + 1}`}
              style={{
                left: `${10 + i * 11}%`,
                animationDelay: `${i * 0.8}s`,
                animationDuration: `${6 + (i % 3) * 2}s`,
              }}
            />
          ))}
        </div>

        {/* Horizontal scan line */}
        <div className="acc__hero-scanline" aria-hidden="true" />

        <div className="acc__hero-content acc__anim acc__anim--1">
          <div className="acc__hero-eyebrow">
            <span className="acc__hero-eyebrow-rule" />
            <span className="acc__hero-eyebrow-text">{t('account.heroTag')}</span>
            <span className="acc__hero-eyebrow-rule" />
          </div>
          <h1 className="acc__hero-title">{t('account.welcomeBack')}, <em>{user?.full_name || t('account.traveler')}</em></h1>
          <p className="acc__hero-sub">{t('account.heroDesc')}</p>
        </div>
      </section>

      {/* ── Stats Ribbon ── */}
      {stats && (
        <section className="acc__stats-section acc__anim acc__anim--2">
          <div className="acc__stats">
            <div className="acc__stat">
              <span className="acc__stat-icon">🏡</span>
              <span className="acc__stat-num">{stats.totalBookings}</span>
              <span className="acc__stat-label">{t('account.statsTotalStays')}</span>
            </div>
            <div className="acc__stat-sep" />
            <div className="acc__stat">
              <span className="acc__stat-icon">🌙</span>
              <span className="acc__stat-num">{stats.totalNights}</span>
              <span className="acc__stat-label">{t('account.statsNights')}</span>
            </div>
            <div className="acc__stat-sep" />
            <div className="acc__stat">
              <span className="acc__stat-icon">📅</span>
              <span className="acc__stat-num">{stats.upcoming}</span>
              <span className="acc__stat-label">{t('account.statsUpcoming')}</span>
            </div>
            <div className="acc__stat-sep" />
            <div className="acc__stat">
              <span className="acc__stat-icon">👥</span>
              <span className="acc__stat-num">{stats.totalGuests}</span>
              <span className="acc__stat-label">{t('account.statsGuests')}</span>
            </div>
            {stats.favouriteMonth && (
              <>
                <div className="acc__stat-sep" />
                <div className="acc__stat">
                  <span className="acc__stat-icon">⭐</span>
                  <span className="acc__stat-num acc__stat-num--text">{stats.favouriteMonth}</span>
                  <span className="acc__stat-label">Favourite Month</span>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* ── Main Grid ── */}
      <section className="acc__main">
        <div className="acc__grid">
          {/* LEFT COLUMN */}
          <div className="acc__left">
            {/* Loyalty Card */}
            {loyalty && (
              <div className="acc__card acc__loyalty acc__anim acc__anim--3">
                <div className="acc__card-head">
                  <span className="acc__card-tag">{t('account.loyaltyTag')}</span>
                </div>
                <div className="acc__loyalty-tier">
                  <div className="acc__loyalty-sigil" style={{ borderColor: TIER_COLORS[loyalty.tier], background: TIER_COLORS[loyalty.tier] + '12' }}>
                    <span className="acc__loyalty-icon">{TIER_ICONS[loyalty.tier]}</span>
                  </div>
                  <div className="acc__loyalty-info">
                    <h3 style={{ color: TIER_COLORS[loyalty.tier] }}>{loyalty.tier}</h3>
                    {loyalty.discount > 0 && (
                      <div className="acc__loyalty-badge" style={{ borderColor: TIER_COLORS[loyalty.tier] + '44', color: TIER_COLORS[loyalty.tier], background: TIER_COLORS[loyalty.tier] + '0a' }}>
                        {loyalty.discount}% {t('account.loyaltyDiscount')}
                      </div>
                    )}
                  </div>
                </div>
                {loyalty.nextTier && (
                  <div className="acc__loyalty-progress">
                    <div className="acc__loyalty-bar">
                      <div className="acc__loyalty-bar-fill" style={{ width: loyalty.progress + '%', background: 'linear-gradient(90deg, ' + TIER_COLORS[loyalty.tier] + '88, ' + TIER_COLORS[loyalty.tier] + ')' }} />
                    </div>
                    <p className="acc__loyalty-next">
                      {loyalty.bookingsToNext} more stay{loyalty.bookingsToNext !== 1 ? 's' : ''} to <strong style={{ color: TIER_COLORS[loyalty.nextTier] }}>{loyalty.nextTier}</strong>
                    </p>
                  </div>
                )}
                <div className="acc__loyalty-roadmap">
      {Object.entries(TIER_ICONS).map(([tier, icon], idx) => {
                    const th = [0, 3, 5, 10, 20];
                    const reached = stats.totalBookings >= th[idx];
                    return (
                      <div key={tier} className={'acc__loyalty-step' + (reached ? ' acc__loyalty-step--done' : '') + (tier === loyalty.tier ? ' acc__loyalty-step--active' : '')}>
                        <span className="acc__loyalty-step-icon">{icon}</span>
                        <span className="acc__loyalty-step-name">{tier}</span>
        <span className="acc__loyalty-step-req">{th[idx]}+ {t('account.loyaltyStays')}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Profile Card */}
            <div className="acc__card acc__profile acc__anim acc__anim--4">
              <div className="acc__card-head">
                <span className="acc__card-tag">{t('account.profileTag')}</span>
                <button className="acc__signout" onClick={doSignOut}>{t('account.profileSignOut')}</button>
              </div>
              <div className="acc__profile-info">
                <div className="acc__profile-row">
                  <span className="acc__profile-row-icon">✉️</span>
                  <div>
                    <span className="acc__profile-row-label">{t('account.profileEmail')}</span>
                    <span className="acc__profile-row-val">{user?.email || '—'}</span>
                  </div>
                </div>
                <div className="acc__profile-row">
                  <span className="acc__profile-row-icon">📆</span>
                  <div>
                    <span className="acc__profile-row-label">{t('account.profileJoined')}</span>
                    <span className="acc__profile-row-val">{memberSince}</span>
                  </div>
                </div>
              </div>
              <form onSubmit={handleSave} className="acc__profile-form">
                {profileMsg.text && <div className={'acc__msg acc__msg--' + profileMsg.type}>{profileMsg.text}</div>}
                <div className="acc__profile-fields">
                  <div className="acc__field">
                    <label>{t('account.profileName')}</label>
                    <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder={t('account.profileName')} />
                  </div>
                  <div className="acc__field">
                    <label>{t('account.profilePhone')}</label>
                    <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder={t('account.profilePhone')} />
                  </div>
                </div>
                <button className="acc__btn acc__btn--save" type="submit" disabled={saving}>
                  {saving ? t('account.profileSaving') : t('account.profileSave')}
                </button>
              </form>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="acc__right acc__anim acc__anim--3">
            <div className="acc__card acc__bookings">
              <div className="acc__card-head">
                <span className="acc__card-tag">{t('account.heroTag')}</span>
                <Link to="/book" className="acc__btn acc__btn--new">+ {t('footer.navigate.book')}</Link>
              </div>
              <div className="acc__tabs">
                {[
                  { key: 'upcoming', label: t('account.tabsUpcoming'), count: bookings.filter(b => { const s = bkStatus(b); return s === 'upcoming' || s === 'active'; }).length },
                  { key: 'past', label: t('account.tabsPast'), count: bookings.filter(b => bkStatus(b) === 'completed').length },
                  { key: 'cancelled', label: t('account.tabsCancelled'), count: bookings.filter(b => bkStatus(b) === 'cancelled').length },
                ].map(t => (
                  <button key={t.key} className={'acc__tab' + (tab === t.key ? ' acc__tab--active' : '')} onClick={() => setTab(t.key)}>
                    {t.label}
                    {t.count > 0 && <span className="acc__tab-count">{t.count}</span>}
                  </button>
                ))}
              </div>
              <div className="acc__booking-list">
                {loading ? (
                  <div className="acc__empty">
                    <div className="acc__spinner" />
                    <p>{t('account.profileSaving') /* fallback to a short loading label if no dedicated key */}</p>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="acc__empty">
                    <span className="acc__empty-icon">{tab === 'upcoming' ? '🌄' : tab === 'past' ? '📖' : '💨'}</span>
                    <p>{t('account.bookingNoStays')}</p>
                    {tab === 'upcoming' && <Link to="/book" className="acc__btn acc__btn--outline">{t('footer.navigate.book')}</Link>}
                  </div>
                ) : filtered.map(bk => {
                  const st = bkStatus(bk);
                  const n = nights(bk.check_in, bk.check_out);
                  return (
                    <div key={bk.id} className={'acc__booking acc__booking--' + st}>
                      <div className="acc__booking-badge">
                        <span className="acc__booking-badge-day">{new Date(bk.check_in).getDate()}</span>
                        <span className="acc__booking-badge-month">{MONTHS[new Date(bk.check_in).getMonth()]}</span>
                      </div>
                      <div className="acc__booking-body">
                        <div className="acc__booking-range">{fmtDate(bk.check_in)} → {fmtDate(bk.check_out)}</div>
                        <div className="acc__booking-meta">
                          <span>🌙 {n} {t('book.nights')}</span>
                          <span>👥 {bk.guests}</span>
                          {bk.notes && <span title={bk.notes}>📝</span>}
                        </div>
                      </div>
                      <div className="acc__booking-end">
                        <span className={'acc__booking-status acc__booking-status--' + st}>
                          {st === 'active' ? '● ' + t('account.statusActive') : st === 'upcoming' ? t('account.statusUpcoming') : st === 'completed' ? '✓ ' + t('account.statusCompleted') : '✕ ' + t('account.statusCancelled')}
                        </span>
                        {st === 'upcoming' && (
                          <button className="acc__booking-cancel" onClick={() => handleCancel(bk.id)} disabled={cancellingId === bk.id}>
                            {cancellingId === bk.id ? '...' : t('account.bookingCancelBtn')}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}